import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Label,
  Input,
  Alert,
  AlertDescription,
  useToast,
} from "@repo/ui";
import { AlertTriangle } from "lucide-react";
import { PasswordConfirmationModal } from "@/components/security/password-confirmation-modal";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { deleteInventory } from "@/lib/api/inventory";
import { ApiClientError } from "@/lib/api/client";

interface DeleteInventoryModalProps {
  open: boolean;
  onClose: () => void;
  inventoryItem: {
    id: string;
    ndc: string;
    item_name: string;
  } | null;
  onSuccess?: () => void;
}

export function DeleteInventoryModal({
  open,
  onClose,
  inventoryItem,
  onSuccess,
}: DeleteInventoryModalProps) {
  const [ndcConfirmation, setNdcConfirmation] = React.useState("");
  const [error, setError] = React.useState("");
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const { toast } = useToast();

  // Authorization approval flow (global modal)
  const { requestApproval, isApprovalPending } = useApproval();
  const passwordRef = React.useRef<string>("");

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setNdcConfirmation("");
      setError("");
    }
  }, [open]);

  const handleClose = () => {
    setNdcConfirmation("");
    setError("");
    onClose();
  };

  const handleNdcConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!inventoryItem) return;

    // Check if NDC matches
    if (ndcConfirmation.trim() !== inventoryItem.ndc) {
      setError("NDC does not match. Please type the exact NDC to confirm.");
      return;
    }

    // Show password confirmation modal
    setShowPasswordModal(true);
  };

  const deleteWithToken = async (password: string, approvalToken?: string) => {
    if (!inventoryItem) return;

    try {
      await deleteInventory({
        id: inventoryItem.id,
        password,
        approvalToken,
      });

      toast({
        title: "Inventory Item Deleted",
        description: `${inventoryItem.item_name} and all related records have been permanently removed.`,
        variant: "success",
      });

      setShowPasswordModal(false);
      onSuccess?.();
      handleClose();
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 403) {
        setShowPasswordModal(false);
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: (token) => deleteWithToken(password, token),
          actionDescription: `Delete inventory item ${inventoryItem.item_name}`,
        });
        return;
      }

      console.error("Error deleting inventory:", error);
      // Re-throw to let password modal handle the error display
      throw error;
    }
  };

  const handlePasswordConfirm = async (password: string) => {
    passwordRef.current = password;
    await deleteWithToken(password);
  };

  const isNdcValid = ndcConfirmation.trim() === inventoryItem?.ndc;

  return (
    <>
      <Dialog
        open={open && !showPasswordModal && !isApprovalPending}
        onOpenChange={handleClose}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-danger" />
              <DialogTitle>Delete Inventory Item</DialogTitle>
            </div>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              inventory item and all associated transaction history.
            </DialogDescription>
          </DialogHeader>

          {inventoryItem && (
            <div className="space-y-4">
              {/* Item Info */}
              <div className="rounded-lg bg-muted p-3 space-y-1">
                <div className="text-sm">
                  <span className="font-medium text-muted-foreground">
                    Item:
                  </span>{" "}
                  <span className="font-semibold">
                    {inventoryItem.item_name}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-muted-foreground">
                    NDC:
                  </span>{" "}
                  <span className="font-mono font-semibold">
                    {inventoryItem.ndc}
                  </span>
                </div>
              </div>

              {/* Warning Alert */}
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Warning:</strong> Deleting this item will permanently
                  remove all receipts, dispenses, and stock adjustments
                  associated with it. This action cannot be undone and will be
                  logged in the audit trail.
                </AlertDescription>
              </Alert>

              {/* NDC Confirmation Form */}
              <form onSubmit={handleNdcConfirm} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ndc-confirm">
                    Type{" "}
                    <span className="font-mono font-bold">
                      {inventoryItem.ndc}
                    </span>{" "}
                    to confirm
                  </Label>
                  <Input
                    id="ndc-confirm"
                    type="text"
                    placeholder="Enter NDC"
                    value={ndcConfirmation}
                    onChange={(e) => {
                      setNdcConfirmation(e.target.value);
                      setError("");
                    }}
                    autoFocus
                    className={error ? "border-danger" : ""}
                  />
                  {error && <p className="text-xs text-danger">{error}</p>}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={!isNdcValid}
                  >
                    Continue to Password
                  </Button>
                </DialogFooter>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Confirmation Modal */}
      {inventoryItem && (
        <PasswordConfirmationModal
          open={showPasswordModal && !isApprovalPending}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={handlePasswordConfirm}
          title="Confirm Deletion"
          description={`Enter your password to permanently delete ${inventoryItem.item_name}`}
          actionLabel="Delete"
        />
      )}
    </>
  );
}
